import React, { useState } from 'react';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, sendPasswordResetEmail, sendEmailVerification } from "firebase/auth";
import { ChevronDown, Lock } from 'lucide-react';

// --- IMPORTS ---
import { useFinancialData } from './hooks/useFinancialData';
import { TABS, THEMES } from './utils/constants';

// --- COMPONENTS ---
import { ElementalLogo } from './components/ui/ElementalLogo';
import { Splash, Onboarding, AuthModal } from './components/ui/Overlays';
import { BadgeNotification } from './components/ui/BadgeNotification';
import { BadgeProfile } from './components/ui/BadgeProfile';
import { NavUserMenu } from './components/ui/NavUserMenu';
import { LegalModal } from './components/ui/LegalModal';
import { GuidedTour } from './components/ui/GuidedTour';
import { EducationModal } from './components/ui/EducationModal';
import { ProfileModal } from './components/ui/ProfileModal';
import { Gatekeeper } from './components/ui/Gatekeeper';

// --- TABS ---
import { DashboardTab } from './components/tabs/DashboardTab';
import { AssetsTab } from './components/tabs/AssetsTab';
import { IncomeTab } from './components/tabs/IncomeTab';
import { ExpensesTab } from './components/tabs/ExpensesTab';
import { DebtTab } from './components/tabs/DebtTab';
import { TrackerTab } from './components/tabs/TrackerTab';
import { WaterfallTab } from './components/tabs/WaterfallTab';

export default function FinancialOS() {
  // --- 1. GATEKEEPER STATE ---
  // Check LocalStorage to see if they've unlocked it on this device
  const [isUnlocked, setIsUnlocked] = useState(() => {
      return localStorage.getItem('sf_app_access_granted') === 'true';
  });

  const { data, loading, user, auth, trackerOffset, actions, calculations, newBadge, simulateVolatility, isSaving } = useFinancialData();
  
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showSplash, setShowSplash] = useState(true);
  const [showInfo, setShowInfo] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showBadges, setShowBadges] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showEducation, setShowEducation] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [isReplayingTour, setIsReplayingTour] = useState(false);

  const currentElement = TABS.find(t => t.id === activeTab)?.element || 'air';
  const theme = THEMES[currentElement];

  // --- 2. GATEKEEPER LOGIC ---
  if (!isUnlocked) {
      return (
          <Gatekeeper onUnlock={() => {
              localStorage.setItem('sf_app_access_granted', 'true');
              setIsUnlocked(true);
          }} />
      );
  }

  // --- AUTH HANDLERS ---
  const handleLogout = async () => { await signOut(auth); };
  
  const handleAuth = async (email, pass, isRegistering) => {
      const isValidPassword = (p) => /^(?=.*[A-Za-z])(?=.*\d)(?=.*[\W_]).{8,}$/.test(p);
      
      if (isRegistering && !isValidPassword(pass)) { 
          alert("Password requirement: 8+ characters, including at least one letter, one number, and one special character."); 
          return; 
      }
      try { 
          if (isRegistering) {
              const userCredential = await createUserWithEmailAndPassword(auth, email, pass);
              await sendEmailVerification(userCredential.user);
              alert("Account created! A verification link has been sent to your email.");
          } else {
              await signInWithEmailAndPassword(auth, email, pass);
          }
          setShowAuthModal(false); 
      } catch (err) { 
          alert(err.message); 
      } 
  };

  const handleReset = async (email) => {
      if (!email) {
          alert("Please enter your email address first.");
          return;
      }
      try {
          await sendPasswordResetEmail(auth, email);
          alert("Password reset email sent! Check your inbox.");
          setShowAuthModal(false);
      } catch (err) {
          alert(err.message);
      }
  };

  // --- TOUR HELPERS ---
  const shouldShowTour = !showSplash && data.hasSeenIntro && !data.hasSeenTour;
  const showTour = shouldShowTour || isReplayingTour;

  const handleTourComplete = () => {
      setIsReplayingTour(false);
      actions.markTourComplete();
  };

  if (loading) return <div className="min-h-screen bg-slate-950 flex items-center justify-center text-white">Loading Elements...</div>;

  return (
    <div className={`min-h-screen bg-slate-950 text-slate-200 font-sans selection:bg-${theme.tailwind}/30 flex flex-col overflow-x-hidden`}>
      
      {/* --- OVERLAYS --- */}
      {showSplash && <Splash onFinish={() => setShowSplash(false)} />}
      {!showSplash && !data.hasSeenIntro && <Onboarding onComplete={() => actions.update('hasSeenIntro', true)} />}
      {showInfo && <Onboarding onComplete={() => setShowInfo(false)} />}
      
      {/* GUIDED TOUR */}
      {showTour && (
          <GuidedTour 
              onComplete={handleTourComplete} 
              activeTab={activeTab} 
              setActiveTab={setActiveTab} 
          />
      )}
      
      {/* GAMIFICATION & LEGAL OVERLAYS */}
      {newBadge && <BadgeNotification badge={newBadge} onClose={actions.clearNewBadge} />}
      
      {showBadges && (
          <BadgeProfile 
              data={data} 
              onClose={() => setShowBadges(false)} 
              onResetBadge={actions.resetBadge} 
          />
      )}
      
      {/* PROFILE MODAL (SOCIAL) */}
      {showProfile && (
          <ProfileModal 
              data={data}
              onClose={() => setShowProfile(false)}
              onSave={actions.updateProfile}
              onAddFriend={actions.addFriend}
              onRemoveFriend={actions.removeFriend}
          />
      )}
      
      {/* TERMS MODAL */}
      {showTerms && <LegalModal onClose={() => setShowTerms(false)} />}
      
      {/* KNOWLEDGE BASE MODAL */}
      {showEducation && (
          <EducationModal 
            onClose={() => setShowEducation(false)} 
            onNavigate={setActiveTab}
            readArticles={data.readArticles} 
            onMarkRead={actions.markArticleRead} 
          />
      )}

      {showAuthModal && (
          <AuthModal 
            onClose={() => setShowAuthModal(false)} 
            onLogin={(e, p) => handleAuth(e, p, false)} 
            onRegister={(e, p) => handleAuth(e, p, true)} 
            onReset={handleReset}
          />
      )}

      {/* HEADER */}
      <nav className={`border-b border-slate-800 bg-slate-950/80 backdrop-blur-md sticky top-0 z-50 transition-all duration-500 border-b-${theme.tailwind}/20`}>
        <div className="max-w-7xl mx-auto px-4 lg:px-6 h-16 flex items-center justify-between gap-4">
          
          {/* LOGO */}
          <div className="flex items-center gap-2 shrink-0">
            <ElementalLogo className={`w-8 h-8 text-${theme.tailwind}`} />
            <span className="font-bold text-lg tracking-tight text-white hidden md:block">
                Four Elements <span className={`text-${theme.tailwind}`}>Finance&trade;</span>
            </span>
          </div>
          
          {/* CONTROLS */}
          <div className="flex items-center gap-4 flex-1 justify-end min-w-0">
              
              {/* --- DESKTOP NAVIGATION (Pills) --- */}
              <div className="hidden md:flex gap-2">
                  {TABS.map(tab => {
                      const isActive = activeTab === tab.id;
                      const tabTheme = THEMES[tab.element];
                      return (
                          <button
                              key={tab.id}
                              id={`nav-${tab.id}`}
                              onClick={() => setActiveTab(tab.id)}
                              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap border ${isActive ? `${tabTheme.bg} ${tabTheme.border} text-${tabTheme.tailwind}` : 'border-transparent text-slate-400 hover:text-white hover:bg-slate-900'}`}
                          >
                              {tab.label}
                          </button>
                      );
                  })}
              </div>

              {/* --- MOBILE NAVIGATION (Dropdown) --- */}
              <div className="md:hidden flex-1 max-w-[200px]">
                  <div className="relative">
                      {/* Custom Arrow Icon */}
                      <ChevronDown className={`absolute right-3 top-2.5 w-4 h-4 text-${theme.tailwind} pointer-events-none`} />
                      
                      <select 
                          value={activeTab} 
                          onChange={(e) => setActiveTab(e.target.value)}
                          className={`w-full appearance-none bg-slate-900 border border-slate-700 text-white text-sm font-bold py-2 pl-3 pr-8 rounded-lg outline-none focus:border-${theme.tailwind} transition-colors uppercase tracking-wider`}
                      >
                          {TABS.map(tab => (
                              <option key={tab.id} value={tab.id}>
                                  {tab.label}
                              </option>
                          ))}
                      </select>
                  </div>
              </div>

              {/* SEPARATOR */}
              <div className="h-8 w-px bg-slate-800 hidden md:block"></div>

              {/* USER MENU */}
              <div id="nav-user-menu">
                  <NavUserMenu 
                      user={user} 
                      data={data}
                      isSaving={isSaving} 
                      onLogin={() => setShowAuthModal(true)} 
                      onLogout={handleLogout}
                      onReset={actions.resetData}
                      onShowBadges={() => setShowBadges(true)}
                      onShowHelp={() => setIsReplayingTour(true)}
                      onShowEducation={() => setShowEducation(true)}
                      onShowProfile={() => setShowProfile(true)}
                      onShowTerms={() => setShowTerms(true)}
                  />
              </div>

          </div>
        </div>
      </nav>

      {/* MAIN CONTENT AREA */}
      <main className="flex-1 max-w-7xl mx-auto px-4 lg:px-6 py-8 transition-all duration-500 w-full">
        {activeTab === 'dashboard' && (
            <DashboardTab 
                data={data} 
                actions={actions} 
                calculations={calculations} 
                theme={theme} 
                setActiveTab={setActiveTab}
                isVolatilityActive={simulateVolatility} 
            />
        )}
        {activeTab === 'allocation' && <AssetsTab data={data} actions={actions} calculations={calculations} />}
        {activeTab === 'income' && <IncomeTab data={data} actions={actions} calculations={calculations} setActiveTab={setActiveTab} />}
        {activeTab === 'waterfall' && <WaterfallTab data={data} actions={actions} calculations={calculations} />}
        {activeTab === 'expenses' && <ExpensesTab data={data} actions={actions} calculations={calculations} />}
        {activeTab === 'debt' && <DebtTab data={data} actions={actions} calculations={calculations} />}
        {activeTab === 'tracker' && <TrackerTab data={data} actions={{...actions, trackerOffset}} calculations={calculations} theme={theme} />}
      </main>

      {/* COPYRIGHT FOOTER */}
      <footer className={`border-t border-slate-800 bg-slate-950 py-8 text-center text-slate-600 text-xs border-t-${theme.tailwind}/20 transition-all duration-500`}>
        <div className="max-w-7xl mx-auto px-4 flex flex-col items-center gap-2">
            <p className="font-medium">&copy; {new Date().getFullYear()} Four Elements Finance&trade;. All Rights Reserved.</p>
            <p className="text-slate-700">Protected by International Copyright Law.</p>
            
            {/* TERMS LINK */}
            <button 
                onClick={() => setShowTerms(true)} 
                className="mt-2 text-[10px] text-slate-500 hover:text-slate-300 underline transition-colors"
            >
                Terms of Service & Disclaimer
            </button>

            {/* --- MANUAL RE-LOCK BUTTON --- */}
            <button 
                onClick={() => { localStorage.removeItem('sf_app_access_granted'); setIsUnlocked(false); }} 
                className="mt-4 text-[9px] text-slate-800 hover:text-red-500 uppercase tracking-widest flex items-center gap-1 transition-colors"
            >
                <Lock className="w-3 h-3" /> Secure Lock
            </button>

            <p className="mt-2 text-[10px] text-slate-800 uppercase tracking-widest">Financial Architecture System v2.0</p>
        </div>
      </footer>
    </div>
  );
}